/**
 * @file useDeviceApi.js
 * @description API composable for device CRUD operations
 * Handles all HTTP requests related to device entities
 */

import { ref, toValue } from 'vue';
import { useQuasar } from 'quasar';
import brickSchemaService from '@/services/brickSchemaService';

/**
 * Composable for device API operations
 * @param {number|Ref<number>} siteId (can be a ref or computed) - The site ID
 * @returns {Object} Device API methods and state
 */
export function useDeviceApi(siteId) {
  const $q = useQuasar();

  // State
  const loading = ref(false);
  const saving = ref(false);

  /**
   * Get a single device by ID with full details (includes device_part, location)
   * @param {number} deviceId - Device ID
   * @returns {Promise<Object|null>} Device object or null
   */
  async function getDevice(deviceId) {
    try {
      loading.value = true;

      const result = await brickSchemaService.devices.getById(toValue(siteId), deviceId);

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data.data || result.data || null;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load device: ${error.message}`,
      });
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Load devices for equipment
   * @param {number} equipmentId - Equipment ID
   * @returns {Promise<Array>} Devices array
   */
  async function loadEquipmentDevices(equipmentId) {
    try {
      loading.value = true;

      const result = await brickSchemaService.devices.getAll(toValue(siteId), { equipment_id: equipmentId });

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data.data || result.data || [];
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load devices: ${error.message}`,
      });
      return [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create new device
   * @param {Object} deviceData - Device data to create
   * @returns {Promise<Object>} Created device
   */
  async function createDevice(deviceData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.devices.create(toValue(siteId), deviceData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newDevice = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Device "${deviceData.name}" created successfully`,
      });

      return newDevice;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create device: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Update existing device
   * @param {number} deviceId - Device ID
   * @param {Object} deviceData - Updated device data
   * @returns {Promise<Object>} Updated device
   */
  async function updateDevice(deviceId, deviceData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.devices.update(toValue(siteId), deviceId, deviceData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const updatedDevice = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: 'Device updated successfully',
      });

      return updatedDevice;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update device: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Delete device
   * @param {number} deviceId - Device ID to delete
   * @param {boolean} cascade - Delete children recursively
   * @returns {Promise<void>}
   */
  async function deleteDevice(deviceId, cascade = false) {
    try {
      const result = await brickSchemaService.devices.delete(toValue(siteId), deviceId, cascade);

      if (!result.success) {
        const error = new Error(result.error);
        // Preserve full response data for cascade dialog detection
        if (result.response) {
          error.response = result.response;
        }
        throw error;
      }
    } catch (error) {
      // Re-throw with response data intact - don't show notification here
      throw error;
    }
  }

  /**
   * Create compound device from template
   * @param {Object} compoundData - Compound device data with template reference
   * @returns {Promise<Object>} Created compound device
   */
  async function createCompoundDevice(compoundData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.devices.createCompound(toValue(siteId), compoundData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newCompound = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Compound device "${compoundData.name}" created successfully`,
      });

      return newCompound;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create compound device: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Duplicate device
   * @param {number} deviceId - Device ID to duplicate
   * @param {string} newName - Name for the duplicated device
   * @returns {Promise<Object>} Duplicated device
   */
  async function duplicateDevice(deviceId, newName) {
    try {
      saving.value = true;

      const result = await brickSchemaService.devices.duplicate(toValue(siteId), deviceId, newName);

      if (!result.success) {
        throw new Error(result.error);
      }

      const duplicatedDevice = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Device duplicated as "${newName}"`,
      });

      return duplicatedDevice;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to duplicate device: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  return {
    // State
    loading,
    saving,

    // Methods
    getDevice,
    loadEquipmentDevices,
    createDevice,
    updateDevice,
    deleteDevice,
    createCompoundDevice,
    duplicateDevice,
  };
}
